"use client";
import React, { useEffect, useState } from "react";
import "~/styles/form.css";
import { X, Shield, ExternalLink } from "lucide-react";
import { analytics } from "lib/segment";
import { usePathname, useSearchParams } from "next/navigation";

declare global {
  interface Window {
    lintrk: any;
  }
}

const HubspotContactForm = ({
  region,
  portalId,
  formId,
  segmentMsg = "Form submitted",
}) => {
  const [noticeIsVisible, setNoticeIsVisible] = useState(false);
  // const [isSuccess, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [os, setOs] = useState<string>("Windows");
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Check if user is on Firefox
    const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");

    if (isFirefox && !window.HubSpotFormsV4) {
      setNoticeIsVisible(true);
    }
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://js.hsforms.net/forms/embed/19886599.js";
    script.defer = true;
    document.body.appendChild(script);

    const handleFormSuccess = (event: Event) => {
      const queryString = searchParams.toString();

      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const currentPath = pathname || window.location.pathname;
      const fullPath = queryString
        ? `${currentPath}?${queryString}`
        : currentPath;

      window.HubSpotFormsV4.getFormFromEvent(event)
        .getFormFieldValues()
        .then((submissionValues: any) => {
          const valuesObj = submissionValues.reduce(
            (acc: Record<string, any>, field: { name: string; value: any }) => {
              acc[field.name] = field.value;
              return acc;
            },
            {},
          );

          setOs(valuesObj["0-1/qndk_operating_system"]);
          if (
            typeof window !== "undefined" &&
            (window.OnetrustActiveGroups == undefined ||
              window.OnetrustActiveGroups?.includes("2"))
          ) {
            // Segment Identify Call
            const dynamicFields = Object.keys(valuesObj).reduce(
              (acc: Record<string, any>, key: string) => {
                const cleanKey = key.replace("0-1/", "");
                acc[cleanKey] = valuesObj[key];
                return acc;
              },
              {},
            );

            analytics.identify({
              ...dynamicFields,
            });

            // Segment Track Call
            analytics.track(segmentMsg, {
              link_label: "Submit",
              location: "body",
              page_base_url: baseUrl,
              page_full_url: baseUrl + fullPath,
              resource_interaction: "form submitted",
            });
          }
        });
    };

    script.addEventListener("load", () => {
      window.addEventListener(
        "hs-form-event:on-submission:success",
        handleFormSuccess,
      );
      setLoading(false);
    });

    return () => {
      document.body.removeChild(script);
      window.removeEventListener(
        "hs-form-event:on-submission:success",
        handleFormSuccess,
      );
    };
  }, [pathname, searchParams]);

  return (
    <>
      {noticeIsVisible && !window.HubSpotFormsV4 ? (
        <div className="border-orange-200 z-50 mt-5 max-w-md rounded-lg border bg-white p-4 shadow-lg">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0">
              <Shield className="text-orange-500 h-5 w-5" />
            </div>

            <div className="min-w-0 flex-1">
              <h3 className="mb-1 text-sm font-semibold text-gray-900">
                Form Blocked by Firefox
              </h3>
              <p className="mb-3 text-sm text-gray-600">
                Firefox's Enhanced Tracking Protection may be blocking this
                form. To submit the form, please temporarily disable tracking
                protection for this site.
              </p>

              <div className="space-y-1 text-xs text-gray-500">
                <p>
                  <strong>To disable:</strong>
                </p>
                <p>1. Click the shield icon in your address bar</p>
                <p>2. Toggle off "Enhanced Tracking Protection"</p>
                <p>3. Refresh the page</p>
              </div>

              <a
                href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                Learn more about Firefox tracking protection
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div>
            {loading && <div className="loader">Loading form...</div>}
            <div
              className="hs-form-frame"
              data-region={region}
              data-form-id={formId}
              data-portal-id={portalId}
            ></div>
          </div>
        </>
      )}
    </>
  );
};

export default HubspotContactForm;
